import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { allArticles } from "@/mocks/blog";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const location = useLocation();
  const guideRef = useRef<HTMLDivElement>(null);
  const catRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) setGuideOpen(false);
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => { setMobileOpen(false); setGuideOpen(false); setCatOpen(false); }, [location]);

  const navBg = scrolled || !isHome
    ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/50"
    : "bg-transparent";
  const textColor = scrolled || !isHome ? "text-foreground-900" : "text-background-50";
  const logoColor = scrolled || !isHome ? "text-foreground-950" : "text-background-50";

  const articleLinks = allArticles.slice(0, 10);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="flex items-center justify-between px-6 md:px-10 lg:px-16 h-16 md:h-18">
        {/* Logo */}
        <Link to="/" className={`font-heading text-lg md:text-xl font-extrabold tracking-tight whitespace-nowrap cursor-pointer ${logoColor}`}>
          CSSBuy<span className="font-normal italic">.Team</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${isHome ? (scrolled ? "text-accent-700 bg-accent-100" : "text-background-50 bg-background-50/15") : "text-accent-700 bg-accent-100"}`}
          >
            Home
          </Link>

          {/* Categories Dropdown */}
          <div className="relative" ref={catRef}>
            <button
              onClick={() => { setCatOpen(!catOpen); setGuideOpen(false); }}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${catOpen ? (scrolled || !isHome ? "bg-background-100 text-foreground-900" : "bg-background-50/20 text-background-50") : (scrolled || !isHome ? "text-foreground-700 hover:bg-background-100" : "text-background-50/85 hover:bg-background-50/10")}`}
            >
              Categorias
              <i className={`ri-arrow-down-s-line text-sm transition-transform w-4 h-4 flex items-center justify-center ${catOpen ? "rotate-180" : ""}`}></i>
            </button>
            {catOpen && (
              <div className="absolute top-full mt-2 left-0 bg-background-50 rounded-xl shadow-lg border border-background-200/50 py-2 w-64 max-h-72 overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-7 h-7 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={cat.image} alt={cat.namePt} className="w-full h-full object-cover" />
                    </span>
                    <span>{cat.namePt}</span>
                    <span className="text-foreground-400 text-xs ml-auto">{cat.count}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Guia de Compras Dropdown */}
          <div className="relative" ref={guideRef}>
            <button
              onClick={() => { setGuideOpen(!guideOpen); setCatOpen(false); }}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${guideOpen ? (scrolled || !isHome ? "bg-background-100 text-foreground-900" : "bg-background-50/20 text-background-50") : (scrolled || !isHome ? "text-foreground-700 hover:bg-background-100" : "text-background-50/85 hover:bg-background-50/10")}`}
            >
              Guia de Compras
              <i className={`ri-arrow-down-s-line text-sm transition-transform w-4 h-4 flex items-center justify-center ${guideOpen ? "rotate-180" : ""}`}></i>
            </button>
            {guideOpen && (
              <div className="absolute top-full mt-2 left-0 bg-background-50 rounded-xl shadow-lg border border-background-200/50 py-2 w-80 max-h-80 overflow-y-auto z-50">
                {articleLinks.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/artigo/${article.slug}`}
                    className="flex items-start gap-3 px-4 py-3 text-sm hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 mt-0.5">
                      <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
                    </span>
                    <div>
                      <span className="text-foreground-800 font-medium block leading-snug">{article.title}</span>
                      <span className="text-foreground-400 text-xs whitespace-nowrap">{article.category} · {article.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* CTA */}
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`ml-2 px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all ${scrolled || !isHome ? "bg-foreground-950 text-background-50 hover:bg-foreground-800" : "bg-background-50 text-foreground-950 hover:bg-background-100"}`}
          >
            CSSBuy Sheet
            <i className="ri-arrow-right-up-line ml-1.5 w-4 h-4 inline-flex items-center justify-center"></i>
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer ${textColor}`}
          aria-label="Menu"
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/50 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-800 font-medium text-sm hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-home-4-line text-lg w-5 h-5 flex items-center justify-center"></i>
              Home
            </Link>

            <div className="px-3 py-2">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2 whitespace-nowrap">Categorias</p>
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs text-foreground-700 hover:bg-background-100 cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-6 h-6 rounded-md overflow-hidden flex-shrink-0">
                      <img src={cat.image} alt={cat.namePt} className="w-full h-full object-cover" />
                    </span>
                    {cat.namePt}
                  </a>
                ))}
              </div>
            </div>

            <div className="px-3 py-2">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2 whitespace-nowrap">Guias de Compra</p>
              {articleLinks.map((article) => (
                <Link
                  key={article.slug}
                  to={`/artigo/${article.slug}`}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-800 hover:bg-background-100 cursor-pointer"
                >
                  <span className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0">
                    <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
                  </span>
                  <span className="leading-snug">{article.title}</span>
                </Link>
              ))}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 mx-3 mt-3 px-5 py-3 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer"
            >
              CSSBuy Sheet
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}